import { UserPlus, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useHubAction } from '@/hooks/use-hub-action';
import { api } from '@/lib/api';

/** The trigger, for the People header, beside enrolling a device. */
export function AddPersonButton({ onClick }: { onClick: () => void }) {
  return (
    <Button size="sm" variant="outline" onClick={onClick}>
      <UserPlus aria-hidden />
      Add someone
    </Button>
  );
}

/**
 * Adding a person, in place.
 *
 * This is the door the console did not have. A person is two rows: the domain
 * one, which an invite already creates, and the sign-in account, which nothing
 * in the console ever created. So somebody invited for a device showed up on
 * this page with a role and a limit and still could not sign in, public sign-up
 * closes forever after the first account, and `Temporary password` answered the
 * exact person it was meant to help with "has never signed in, so there is no
 * account to set a password on". Every one of those sentences was true. The
 * verb was missing.
 *
 * Two phases, the same shape `EnrollComposer` uses and for the same reason: the
 * second one is the point. The password is generated by the hub, shown once,
 * and stored nowhere, so a form that closed on success would eat the only copy.
 * It is generated rather than typed because an admin inventing one at the
 * keyboard is how a placeholder becomes somebody's real credential, and the
 * account is born needing a change either way.
 */
export function AddPersonComposer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [made, setMade] = useState<{ email: string; password: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const first = useRef<HTMLInputElement>(null);

  const create = useHubAction(
    async () => {
      const address = email.trim();
      const result = await api.createPerson({
        email: address,
        name: name.trim() || undefined,
        isAdmin,
      });
      setMade({ email: address, password: result.password });
      return result;
    },
    // The composer stays open to show the password, and prints a refusal under
    // its own buttons rather than in a toast over the field it is about.
    { inlineError: true },
  );

  useEffect(() => {
    if (open && !made) first.current?.focus();
  }, [open, made]);

  const close = () => {
    onClose();
    setMade(null);
    setCopied(false);
    setEmail('');
    setName('');
    setIsAdmin(false);
  };

  if (!open) return null;

  return (
    <section className="bg-card overflow-hidden rounded-lg border">
      <div className="bg-sunken flex items-center gap-2 border-b px-gutter py-[calc(var(--dy-row-py)+1px)]">
        <h2 className="text-sm font-medium">Add someone</h2>
        <span className="text-fg-3 text-2xs">an account they can sign in with</span>
        <Button
          variant="ghost"
          size="icon-xs"
          className="ml-auto"
          onClick={close}
          aria-label="Close"
        >
          <X />
        </Button>
      </div>

      {made ? (
        <div className="space-y-2 px-gutter py-3">
          <p className="text-fg-2 text-xs">
            Give this to {made.email}. It is shown once and the hub keeps no copy; they will be
            asked to choose their own password the first time they sign in.
          </p>
          <pre className="bg-sunken dy-scroll overflow-x-auto rounded-md border px-2.5 py-2 font-mono text-sm select-all">
            {made.password}
          </pre>
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                // A refused clipboard (no permission, an insecure origin) is not
                // an error to report: the password is on screen to select.
                void navigator.clipboard
                  ?.writeText(made.password)
                  .then(() => setCopied(true))
                  .catch(() => undefined);
              }}
            >
              {copied ? 'Copied' : 'Copy password'}
            </Button>
            <Button size="sm" variant="ghost" onClick={close}>
              Done
            </Button>
          </div>
        </div>
      ) : (
        <form
          className="space-y-2.5 px-gutter py-3"
          onKeyDown={(event) => {
            if (event.key === 'Escape') close();
          }}
          onSubmit={(event) => {
            event.preventDefault();
            create.run();
          }}
        >
          <div className="grid gap-2.5 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="ap-email">Email</Label>
              <Input
                ref={first}
                id="ap-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                placeholder="santosh.oruganty@nymbl.app"
              />
              <p className="text-fg-3 text-2xs">What they sign in with.</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="ap-name">Name</Label>
              <Input
                id="ap-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Santosh Oruganty"
              />
              <p className="text-fg-3 text-2xs">Optional. The email stands in until they say.</p>
            </div>
          </div>

          <label className="text-fg-2 flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={isAdmin}
              onChange={(event) => setIsAdmin(event.target.checked)}
              className="size-3.5"
            />
            Make them an admin
            <span className="text-fg-3">
              they can approve plans, mint invites and add people themselves
            </span>
          </label>

          {create.error ? (
            <p className="text-bad text-xs" role="alert">
              {create.error}
            </p>
          ) : null}

          <div className="flex items-center gap-1.5">
            <Button type="submit" size="sm" disabled={create.busy}>
              {create.busy ? 'Adding…' : 'Add and make a password'}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={close}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}
